import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isBlueHQ, screenInProduct } from '../shared/site.ts';
import { DeputyHome } from './Home.tsx';
import { ActivePapersSearch, EquipmentRequestPage, EvictionsPage, OnCallPage, PhoneNumbersPage, UpcomingCourt } from '../ops/FieldInfo.tsx';
import { NewTow, TowDetail, TowsPage } from '../ops/Tows.tsx';
import { NewNotice, NoticeDetail, NoticesPage, PioItem, PioPage, PioSettings, RecipientsPage, TipDetail, TipsPage } from '../ops/Pio.tsx';
import { EvaluationDetail, EvaluationsPage, HrEmployee, HrEmployees, InspectionHistory, InspectionsPage, NewInspection, PayPlan, PositionsPage } from '../ops/Hr.tsx';
import { EwsDashboard, EwsOfficer, EwsSettings, MyRecord, PersonnelDetail, PersonnelList, UofList, UofReport } from '../ops/Personnel.tsx';
import { CaseActivity, IntelDetail, IntelPage, NewIntel } from '../ops/Intel.tsx';
import { MyTraining, TrainingClass, TrainingClasses, TrainingMrn, TrainingOfficer, TrainingOfficers, TrainingReview, TrainingRoster, QualRoster } from '../ops/Training.tsx';
import { NewTicket, TicketDetail, TicketsPage } from '../ops/Tickets.tsx';
import { Icon } from '../shared/icons.tsx';
import { CustomizeServe, useApplyLook, useServeLayout } from './serveLayout.tsx';
import { SetOutScreen } from './SetOuts.tsx';
import {
  estimateService,
  evaluateAttempt,
  eligibilityToday,
  type EligibilityToday,
  getPaperType,
  localParts,
  nonServiceProgress,
  normalizeScan,
  OUTCOME_LABELS,
  phaseOf,
  validateSubstituteRecipient,
  vacateStatus,
  windowTracker,
  type AttemptOutcome,
  type AttemptRecord,
  type PaperStatus,
  type RuleSetConfig,
} from '@civilos/domain';
import { api, ApiError } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { AffidavitPanel } from '../shared/affidavit.tsx';
import { fmtDate, fmtDateTime, newId } from '../shared/format.ts';
import { RulesPanel } from '../shared/rules-panel.tsx';
import { match, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, StatusBadge, useAction } from '../shared/ui.tsx';
import { idb, type OutboxOp } from './store.ts';
import { submitAttempt, syncNow, type SyncSummary } from './sync.ts';
import { FieldMap } from './FieldMap.tsx';
import { CheckInOverlay, DutyBar, DutyProvider, OnSceneControls, useDuty } from './duty.tsx';
import { RouteMode } from './RouteMode.tsx';
import { IssuedCard, ResourcesPage, SecurityPage, SignaturePage, StatsPage } from './DeputyAdmin.tsx';
import { passkeysSupported, signInWithPasskey } from '../shared/passkey.tsx';
import { ReportsPage } from '../rms/ReportsPage.tsx';
import { ReportPrint } from '../rms/ReportPrint.tsx';
import { AlertsSetup, NarcanPage } from '../ops/Alerts.tsx';
import { SchoolsPage, SchoolDetail, DrillPage, DrillPrint } from '../ops/Schools.tsx';
import { FormsPage, NewForm, FormDetail, FormPrint } from '../ops/Forms.tsx';
import { TowPrint, TicketPrint } from '../ops/PaperPrints.tsx';
import { EscortsPage, NewEscort, EscortDetail } from '../ops/Escorts.tsx';
import { BolosPage, NewBolo, BoloDetail } from '../ops/Bolos.tsx';
import { ApplicantsPage, NewApplicant, ApplicantDetail } from '../ops/Applicants.tsx';
import { FoiaPage, NewFoia, FoiaSettings, FoiaDetail } from '../ops/Foia.tsx';
import { BwcPage, NewBwc, BwcDetail } from '../ops/Bwc.tsx';
import { EventsPage, NewEvent, EventDetail, SchoolDetailsPage, NewSchoolDetail } from '../ops/Events.tsx';
import { DeskReportsPage, NewDeskReport, DeskReportDetail, SafetyPage, NewSafety, SafetyDetail } from '../ops/SroPages.tsx';
import { ShutoffsPage, OutsideContactsCard } from '../ops/Shutoffs.tsx';
import { HouseCheckDetail, HouseChecksPage } from '../ops/HouseChecks.tsx';
import { DailyLogsPage, DailyLogView, MyDailyLogPage } from '../ops/DailyLog.tsx';
import { IaCaseDetail, IaListPage, NewIaCase } from '../ops/IA.tsx';
import { SimilarCasesPage } from '../rms/SimilarCases.tsx';
import { FieldMapPage } from '../ops/FieldMap.tsx';
import { ResourcesPage } from '../ops/Resources.tsx';
import { CommandBoardPage, CommandBoardsPage, NewCommandBoard } from '../ops/Command.tsx';
import { ArrestFeedPage } from '../ops/ArrestFeed.tsx';
import { PoliciesPage, NewPolicy, PolicyDetail, PolicyImport } from '../ops/Policies.tsx';
import { MileagePage } from '../ops/Mileage.tsx';
import { CallNumbersPage } from '../ops/CallNumbers.tsx';
import { AssetReportPage } from '../ops/AssetReport.tsx';
import { DuesPage, MyDuesPage } from '../ops/Dues.tsx';

import { ReportWizard } from '../rms/ReportWizard.tsx';
import { CrimeAnalytics } from '../rms/Analytics.tsx';
import { SupplementEditor } from '../rms/SupplementEditor.tsx';
import { PeopleSearch, PersonProfile } from '../office/People.tsx';
import { EvidenceItem, EvidenceLabels, EvidencePage } from '../rms/Evidence.tsx';
import { SchedulePage } from './SchedulePage.tsx';
import { FieldHeader, FieldMenu, FieldSide, titleFor } from './shell.tsx';
import { Compose, MailPage, MailThread } from '../ops/Mail.tsx';
import { DirectoryPage } from '../ops/Directory.tsx';
import { CourtEvent, CourtPage } from '../ops/Court.tsx';
import { CaseFileDetail, CaseFilesPage, CaseTrack, MyCasesPage } from '../ops/Cases.tsx';
import { EquipmentDetail, MyGearPage, SparesPage, VehicleDetail } from '../ops/Fleet.tsx';
import { PermitDetail, PermitsPage } from '../ops/Permits.tsx';
import { PhotoFolders, PhotosPage } from '../ops/Photos.tsx';
import { MyWarrants, WarrantDetail, WarrantEntry, WarrantQueue, WarrantRequestDetail, WarrantRequestForm, WarrantsActive } from '../ops/Warrants.tsx';
import { SafetyAlerts } from './alerts.tsx';
import { VehiclesInYard, vehiclesPayload, type VehicleDraft } from './vehicles.tsx';
import { ArrivalScreen, sameAddress, useArrival } from './arrival.tsx';
import { pinOf, usePosition, wontCount } from './position.ts';
import { orderFor, useWorkMode, WorkModeBar, WorkModePicker } from './workmode.tsx';
import { ServiceCircles } from './circles.tsx';
import { looksLikePaperNumber, useGlobalScanner } from '../shared/scanner.ts';

const ROUTE_MIN_WIDTH = 1100;
/** Wide enough to keep the menu open as a column on the left. */
const SIDE_MIN_WIDTH = 900;
const SIDE_KEY = 'ch-field-side';
function useSideMenu() {
  const [roomy, setRoomy] = useState(() => window.innerWidth >= SIDE_MIN_WIDTH);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(SIDE_KEY) === 'hidden';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const on = () => setRoomy(window.innerWidth >= SIDE_MIN_WIDTH);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const toggle = () =>
    setHidden((h) => {
      try {
        localStorage.setItem(SIDE_KEY, h ? 'shown' : 'hidden');
      } catch {
        /* private window */
      }
      return !h;
    });
  return { roomy, open: roomy && !hidden, toggle };
}

function useWide() {
  const [wide, setWide] = useState(() => window.innerWidth >= ROUTE_MIN_WIDTH);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= ROUTE_MIN_WIDTH);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}
const PHONE_PREF = 'civilos.phoneView';
function readPhonePref() {
  try {
    return localStorage.getItem(PHONE_PREF) === '1';
  } catch {
    return false;
  }
}
function writePhonePref(v: boolean) {
  try {
    localStorage.setItem(PHONE_PREF, v ? '1' : '0');
  } catch {
    /* private window: preference just isn't remembered */
  }
}

// ─── Local state hook ────────────────────────────────────────────────────────
function useFieldData() {
  const [papers, setPapers] = useState<any[]>([]);
  const [outbox, setOutbox] = useState<OutboxOp[]>([]);
  const [rules, setRules] = useState<Record<string, RuleSetConfig>>({});
  const [last, setLast] = useState<SyncSummary | null>(null);
  const [pickups, setPickups] = useState<any[]>([]);
  const reload = useCallback(async () => {
    setPickups(((await idb.get('meta', 'pickups')) as any[]) ?? []);
    setPapers(await idb.all('papers'));
    setOutbox(await idb.all('outbox'));
    setRules(((await idb.get('meta', 'rules')) as Record<string, RuleSetConfig>) ?? {});
    setLast(((await idb.get('meta', 'lastSync')) as SyncSummary) ?? null);
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);
  return { papers, outbox, rules, last, pickups, reload };
}

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Offline-capable evaluation using the synced rule set and pending local attempts. */
function localEvaluation(paper: any, rules: Record<string, RuleSetConfig>, pending: OutboxOp[], at = new Date()) {
  const cfg = rules[paper.ruleVersionId];
  if (!cfg) return paper.evaluation;
  const attempts: AttemptRecord[] = [
    ...paper.attempts.map((a: any) => ({ id: a.id, attemptedAt: new Date(a.attempted_at), outcome: a.outcome, method: a.method, voided: a.review_status === 'needs_review' || a.review_status === 'rejected' })),
    ...pending.filter((o) => o.paperId === paper.id && o.state === 'pending').map((o) => ({ id: o.clientId, attemptedAt: new Date(o.payload.attemptedAt as string), outcome: o.payload.outcome as AttemptOutcome, method: (o.payload.method as any) ?? null })),
  ];
  const type = getPaperType(cfg, paper.paperTypeCode);
  const overrideToday = paper.calendarOverrideDate === localParts(at, cfg.timezone).date;
  const ctx = { paperTypeCode: paper.paperTypeCode, phase: phaseOf(paper.status as PaperStatus), onHold: paper.onHold, recalled: paper.recalled, attempts };
  const now = evaluateAttempt(cfg, ctx, { at, calendarOverride: overrideToday });
  const attemptable = ['ASSIGNED', 'IN_SERVICE'].includes(paper.status);
  const doorHanger = paper.attempts.some((a: any) => a.door_hanger) || pending.some((o) => o.paperId === paper.id && o.state === 'pending' && o.payload.doorHanger === true);
  return {
    ...paper.evaluation,
    attemptNow: { ...now, allowed: now.allowed && attemptable, notAssigned: !attemptable },
    nonService: nonServiceProgress(cfg, type, attempts),
    windows: windowTracker(cfg, type, attempts),
    vacate: vacateStatus(cfg, type, attempts, at),
    doorHanger,
    local: true,
    attempts,
  };
}

/** Eligible now / later today / not today, with pending (unsynced) attempts counted. */
function eligibilityNow(paper: any, rules: Record<string, RuleSetConfig>, pending: OutboxOp[], at: Date): EligibilityToday {
  const cfg = rules[paper.ruleVersionId];
  if (!['ASSIGNED', 'IN_SERVICE'].includes(paper.status)) return { bucket: 'notToday', why: 'Not open for service' };
  if (!cfg) return paper.evaluation?.attemptNow?.allowed ? { bucket: 'now' } : { bucket: 'notToday' };
  const attempts: AttemptRecord[] = [
    ...paper.attempts.map((a: any) => ({ id: a.id, attemptedAt: new Date(a.attempted_at), outcome: a.outcome, method: a.method, voided: a.review_status === 'needs_review' || a.review_status === 'rejected' })),
    ...pending.filter((o) => o.paperId === paper.id && o.state === 'pending').map((o) => ({ id: o.clientId, attemptedAt: new Date(o.payload.attemptedAt as string), outcome: o.payload.outcome as AttemptOutcome, method: (o.payload.method as any) ?? null })),
  ];
  return eligibilityToday(cfg, { paperTypeCode: paper.paperTypeCode, phase: phaseOf(paper.status as PaperStatus), onHold: paper.onHold, recalled: paper.recalled, attempts }, at, paper.calendarOverrideDate);
}

// ─── Shell ───────────────────────────────────────────────────────────────────
export function FieldApp() {
  const { me, logout, refresh } = useAuth();
  const { path, navigate } = useRouter();
  const online = useOnline();
  const data = useFieldData();
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  // Good news fades on its own; problems stay until the next sync.
  useEffect(() => {
    if (syncMsg?.tone !== 'ok') return;
    const t = setTimeout(() => setSyncMsg((m) => (m?.tone === 'ok' ? null : m)), 5000);
    return () => clearTimeout(t);
  }, [syncMsg]);
  const [locked, setLocked] = useState<false | 'idle' | 'manual'>(false);
  const idle = useRef(Date.now());
  const wide = useWide();
  const [phoneView, setPhoneView] = useState(readPhonePref);
  const [menu, setMenu] = useState(false);
  const side = useSideMenu();
  const serveLayout = useServeLayout(online);
  useApplyLook(serveLayout.layout);
  const [customizing, setCustomizing] = useState(false);

  const doSync = useCallback(async () => {
    // Only deputies who serve civil papers have a route to sync; patrol and evidence techs don't.
    if (!me?.permissions.includes('attempts.record')) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const s = await syncNow();
      if (s.wiped) {
        setSyncMsg({ tone: 'bad', text: 'This device was remotely wiped by an administrator.' });
        await logout();
        return;
      }
      // Deputies with no civil papers (patrol) don't need to hear about an empty route.
      if (!s.papers && !s.documentsCached && !s.pushed && !s.held && !s.rejected) {
        setSyncMsg(null);
        return;
      }
      const parts = [`Route ready for offline use — ${s.papers} paper(s), ${s.documentsCached} new document(s) cached.`];
      if (s.pushed) parts.push(`${s.pushed} queued attempt(s) uploaded.`);
      if (s.held) parts.push(`${s.held} held for supervisor review.`);
      if (s.rejected) parts.push(`${s.rejected} rejected.`);
      setSyncMsg({ tone: s.held || s.rejected ? 'warn' : 'ok', text: parts.join(' ') });
    } catch (e) {
      setSyncMsg({ tone: 'bad', text: e instanceof ApiError ? e.message : 'Sync failed — no connection. Your work is saved on this device.' });
    } finally {
      setSyncing(false);
      data.reload();
    }
  }, [data, logout, me]);

  // Initial and reconnect sync.
  useEffect(() => {
    if (navigator.onLine) doSync();
  }, []);
  useEffect(() => {
    if (online && data.outbox.some((o) => o.state === 'pending')) doSync();
  }, [online]);

  // MOB-015 auto-lock after inactivity.
  useEffect(() => {
    const bump = () => (idle.current = Date.now());
    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump));
    const t = setInterval(() => {
      if (Date.now() - idle.current > (me?.agency.autoLockMinutes ?? 10) * 60_000) setLocked('idle');
    }, 15_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(t);
    };
  }, [me]);

  // Re-evaluate every minute so "eligible now / later today" stays current as the day goes on.
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000));
  useEffect(() => {
    const t = setInterval(() => setMinute(Math.floor(Date.now() / 60_000)), 15_000);
    return () => clearInterval(t);
  }, []);
  const rows = useMemo(
    () => data.papers.map((x) => ({ p: x, ev: localEvaluation(x, data.rules, data.outbox), elig: eligibilityNow(x, data.rules, data.outbox, new Date()) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.papers, data.rules, data.outbox, minute],
  );
  const { pos, err: posErr } = usePosition();
  const pickupSet = useMemo(() => new Set<string>(data.pickups.filter((x) => x.source === 'office').map((x) => x.paper_id)), [data.pickups]);
  // Papers still at the office can't be served, so they never trigger the arrival screen.
  const arrival = useArrival(useMemo(() => rows.filter((r) => !pickupSet.has(r.p.id)), [rows, pickupSet]), pos);
  const routeMode = wide && !phoneView;
  // Scan a paper barcode from any screen → that paper (from the papers on this device, works offline).
  useGlobalScanner((code) => {
    const n = looksLikePaperNumber(code);
    const hit = n && data.papers.find((x) => x.civilosNumber === n);
    if (hit) navigate(`/field/paper/${hit.id}`);
    else setSyncMsg({ tone: 'warn', text: n ? `${n} isn't assigned to you on this device. If you just received it, sync first.` : `Scanned “${code}” — not a paper barcode.` });
  }, path.split('?')[0] !== '/field/scan');

  const pending = data.outbox.filter((o) => o.state === 'pending').length;
  const flagged = data.outbox.filter((o) => o.state !== 'pending');
  const p = path.split('?')[0]!;
  let m: Record<string, string> | null;
  let screen: ReactNode;
  if ((m = match('/field/paper/:id/attempt', p))) screen = <AttemptForm id={m.id!} data={data} online={online} />;
  else if ((m = match('/field/paper/:id', p)))
    screen = <FieldPaper id={m.id!} data={data} online={online} onChanged={doSync} onCompleted={(msg) => (navigate('/field'), doSync().then(() => setSyncMsg({ tone: 'ok', text: msg })))} />;
  else if (p === '/field/schedule') screen = <SchedulePage online={online} />;
  else if ((m = match('/field/setout/:id', p))) screen = <SetOutScreen id={m.id!} online={online} />;
  else if (p === '/field/home') screen = isBlueHQ() ? <DeputyHome online={online} /> : <StatsPage online={online} />;
  else if (p === '/field/stats') screen = <StatsPage online={online} />;
  else if (p === '/field/pio') screen = <PioPage />;
  else if (p === '/field/pio/notices') screen = <NoticesPage />;
  else if (p === '/field/pio/notices/new') screen = <NewNotice />;
  else if ((m = match('/field/pio/notices/:id', p))) screen = <NoticeDetail key={m.id} id={m.id!} />;
  else if (p === '/field/pio/recipients') screen = <RecipientsPage />;
  else if (p === '/field/hr/applicants') screen = <ApplicantsPage />;
  else if (p === '/field/hr/applicants/new') screen = <NewApplicant />;
  else if ((m = match('/field/hr/applicants/:id', p))) screen = <ApplicantDetail key={m.id} id={m.id!} />;
  else if (p === '/field/pio/foia') screen = <FoiaPage />;
  else if (p === '/field/pio/foia/new') screen = <NewFoia />;
  else if (p === '/field/pio/foia/settings') screen = <FoiaSettings />;
  else if ((m = match('/field/pio/foia/:id', p))) screen = <FoiaDetail key={m.id} id={m.id!} />;
  else if (p === '/field/bwc') screen = <BwcPage />;
  else if (p === '/field/bwc/new') screen = <NewBwc />;
  else if ((m = match('/field/bwc/:id', p))) screen = <BwcDetail key={m.id} id={m.id!} />;
  else if (p === '/field/events') screen = <EventsPage />;
  else if (p === '/field/events/new') screen = <NewEvent />;
  else if ((m = match('/field/events/:id', p))) screen = <EventDetail key={m.id} id={m.id!} />;
  else if (p === '/field/schools/details') screen = <SchoolDetailsPage />;
  else if (p === '/field/schools/details/new') screen = <NewSchoolDetail />;
  else if (p === '/field/schools/desk') screen = <DeskReportsPage />;
  else if (p === '/field/schools/desk/new') screen = <NewDeskReport />;
  else if ((m = match('/field/schools/desk/:id', p))) screen = <DeskReportDetail key={m.id} id={m.id!} />;
  else if (p === '/field/schools/safety') screen = <SafetyPage />;
  else if (p === '/field/schools/safety/new') screen = <NewSafety />;
  else if ((m = match('/field/schools/safety/:id', p))) screen = <SafetyDetail key={m.id} id={m.id!} />;
  else if (p === '/field/dues') screen = <DuesPage />;
  else if (p === '/field/dues/me') screen = <MyDuesPage />;
  else if (p === '/field/call-numbers') screen = <CallNumbersPage />;
  else if (p === '/field/mileage') screen = <MileagePage />;
  else if (p === '/field/policies') screen = <PoliciesPage />;
  else if (p === '/field/policies/new') screen = <NewPolicy />;
  else if (p === '/field/policies/import') screen = <PolicyImport />;
  else if ((m = match('/field/policies/:id', p))) screen = <PolicyDetail key={m.id} id={m.id!} />;
  else if (p === '/field/shutoffs') screen = <ShutoffsPage />;
  else if (p === '/field/arrests') screen = <ArrestFeedPage />;
  else if (p === '/field/pio/settings') screen = <PioSettings />;
  else if ((m = match('/field/pio/:id', p))) screen = <PioItem key={m.id} id={m.id!} />;
  else if (p === '/field/tips') screen = <TipsPage />;
  else if ((m = match('/field/tips/:id', p))) screen = <TipDetail key={m.id} id={m.id!} />;
  else if (p === '/field/training') screen = <MyTraining />;
  else if (p === '/field/training/officers') screen = <TrainingOfficers />;
  else if (p === '/field/training/roster') screen = <TrainingRoster />;
  else if (p === '/field/alerts') screen = <><AlertsSetup /><div className="ops stack"><OutsideContactsCard /></div></>;
  else if (p === '/field/narcan') screen = <NarcanPage />;
  else if (p === '/field/training/quals') screen = <QualRoster />;
  else if ((m = match('/field/training/officers/:id/mrn', p))) screen = <TrainingMrn key={m.id} id={m.id!} />;
  else if ((m = match('/field/training/officers/:id', p))) screen = <TrainingOfficer key={m.id} id={m.id!} />;
  else if (p === '/field/training/review') screen = <TrainingReview />;
  else if (p === '/field/training/classes') screen = <TrainingClasses />;
  else if ((m = match('/field/training/classes/:id', p))) screen = <TrainingClass key={m.id} id={m.id!} />;
  else if (p === '/field/tows') screen = <TowsPage />;
  else if (p === '/field/civil-search') screen = <ActivePapersSearch />;
  else if (p === '/field/evictions') screen = <EvictionsPage />;
  else if (p === '/field/court/upcoming') screen = <UpcomingCourt />;
  else if (p === '/field/on-call') screen = <OnCallPage />;
  else if (p === '/field/phones') screen = <PhoneNumbersPage />;
  else if (p === '/field/equipment/request') screen = <EquipmentRequestPage />;
  else if (p === '/field/tows/new') screen = <NewTow />;
  else if ((m = match('/field/tows/:id', p))) screen = <TowDetail key={m.id} id={m.id!} />;
  else if (p === '/field/bolos') screen = <BolosPage />;
  else if (p === '/field/bolos/new') screen = <NewBolo />;
  else if ((m = match('/field/bolos/:id', p))) screen = <BoloDetail key={m.id} id={m.id!} />;
  else if (p === '/field/patrol-map') screen = <FieldMapPage />;
  else if (p === '/field/resources') screen = <ResourcesPage />;
  else if (p === '/field/command') screen = <CommandBoardsPage />;
  else if (p === '/field/command/new') screen = <NewCommandBoard />;
  else if ((m = match('/field/command/:id', p))) screen = <CommandBoardPage key={m.id} id={m.id!} />;
  else if (p === '/field/daily-log') screen = <MyDailyLogPage />;
  else if (p === '/field/daily-logs') screen = <DailyLogsPage />;
  else if ((m = match('/field/daily-log/:id', p))) screen = <DailyLogView key={m.id} id={m.id!} />;
  else if (p === '/field/house-checks') screen = <HouseChecksPage />;
  else if ((m = match('/field/house-checks/:id', p))) screen = <HouseCheckDetail key={m.id} id={m.id!} />;
  else if (p === '/field/escorts') screen = <EscortsPage />;
  else if (p === '/field/escorts/new') screen = <NewEscort />;
  else if ((m = match('/field/escorts/:id', p))) screen = <EscortDetail key={m.id} id={m.id!} />;
  else if (p === '/field/schools') screen = <SchoolsPage />;
  else if ((m = match('/field/schools/drills/:id', p))) screen = <DrillPage key={m.id} id={m.id!} />;
  else if ((m = match('/field/schools/:id', p))) screen = <SchoolDetail key={m.id} id={m.id!} />;
  else if (p === '/field/forms') screen = <FormsPage />;
  else if (p === '/field/forms/new') screen = <NewForm />;
  else if ((m = match('/field/forms/:id', p))) screen = <FormDetail key={m.id} id={m.id!} />;
  else if (p === '/field/tickets') screen = <TicketsPage />;
  else if (p === '/field/tickets/new') screen = <NewTicket />;
  else if ((m = match('/field/tickets/:id', p))) screen = <TicketDetail key={m.id} id={m.id!} />;
  else if (p === '/field/resources') screen = <ResourcesPage online={online} />;
  else if (p === '/field/signature' || p === '/field/admin') screen = <SignaturePage />;
  else if (p === '/field/security') screen = <SecurityPage />;
  else if (p === '/field/reports') screen = <ReportsPage />;
  else if (p === '/field/people') screen = <PeopleSearch />;
  else if (p === '/field/people/profile') screen = <PersonProfile />;
  else if (p === '/field/reports/analytics') screen = <CrimeAnalytics />;
  else if (p === '/field/reports/similar') screen = <SimilarCasesPage />;
  else if (p === '/field/reports/arrests') screen = <ArrestFeedPage />;
  else if (p === '/field/evidence') screen = <EvidencePage />;
  else if ((m = match('/field/evidence/:id', p))) screen = <EvidenceItem key={m.id} id={m.id!} />;
  else if ((m = match('/field/reports/supplements/:id', p))) screen = <SupplementEditor key={m.id} id={m.id!} />;
  else if ((m = match('/field/reports/:id', p))) screen = <ReportWizard key={m.id} id={m.id!} />;
  else if (p === '/field/issued') screen = <IssuedCard />;
  else if (p === '/field/warrants') screen = <WarrantsActive />;
  else if (p === '/field/warrants/request') screen = <WarrantRequestForm />;
  else if (p === '/field/warrants/mine') screen = <MyWarrants />;
  else if (p === '/field/warrants/queue') screen = <WarrantQueue />;
  else if (p === '/field/warrants/entry') screen = <WarrantEntry key={path} />;
  else if ((m = match('/field/warrants/requests/:id', p))) screen = <WarrantRequestDetail key={m.id} id={m.id!} />;
  else if ((m = match('/field/warrants/:id', p))) screen = <WarrantDetail key={m.id} id={m.id!} />;
  else if (p === '/field/messages') screen = <MailPage />;
  else if (p === '/field/photos') screen = <PhotosPage key={path} />;
  else if ((m = match('/field/photos/case/:id', p))) screen = <PhotoFolders key={m.id} incidentId={m.id!} />;
  else if (p === '/field/messages/new') screen = <Compose />;
  else if ((m = match('/field/messages/:id', p))) screen = <MailThread key={m.id} id={m.id!} />;
  else if (p === '/field/directory') screen = <DirectoryPage />;
  else if (p === '/field/cases') screen = <MyCasesPage />;
  else if (p === '/field/cases/activity') screen = <CaseActivity />;
  else if (p === '/field/hr') screen = <HrEmployees />;
  else if (p === '/field/hr/me') screen = <HrEmployee />;
  else if (p === '/field/hr/pay-plan') screen = <PayPlan />;
  else if (p === '/field/hr/positions') screen = <PositionsPage />;
  else if ((m = match('/field/hr/employees/:id', p))) screen = <HrEmployee key={m.id} id={m.id!} />;
  else if (p === '/field/evaluations') screen = <EvaluationsPage />;
  else if ((m = match('/field/evaluations/:id', p))) screen = <EvaluationDetail key={m.id} id={m.id!} />;
  else if (p === '/field/inspections') screen = <InspectionsPage />;
  else if ((m = match('/field/inspections/:id/new', p))) screen = <NewInspection key={m.id} vehicleId={m.id!} />;
  else if ((m = match('/field/inspections/:id', p))) screen = <InspectionHistory key={m.id} vehicleId={m.id!} />;
  else if (p === '/field/uof') screen = <UofList />;
  else if ((m = match('/field/uof/:id', p))) screen = <UofReport key={m.id} id={m.id!} />;
  else if (p === '/field/personnel') screen = <PersonnelList />;
  else if (p === '/field/personnel/me') screen = <MyRecord />;
  else if ((m = match('/field/personnel/:id', p))) screen = <PersonnelDetail key={m.id} id={m.id!} />;
  else if (p === '/field/ews') screen = <EwsDashboard />;
  else if (p === '/field/ia') screen = <IaListPage />;
  else if (p === '/field/ia/new') screen = <NewIaCase />;
  else if ((m = match('/field/ia/:id', p))) screen = <IaCaseDetail key={m.id} id={m.id!} />;
  else if (p === '/field/ews/settings') screen = <EwsSettings />;
  else if ((m = match('/field/ews/officers/:id', p))) screen = <EwsOfficer key={m.id} id={m.id!} />;
  else if (p === '/field/intel') screen = <IntelPage />;
  else if (p === '/field/intel/new') screen = <NewIntel />;
  else if ((m = match('/field/intel/:id', p))) screen = <IntelDetail key={m.id} id={m.id!} />;
  else if ((m = match('/field/cases/:id', p))) screen = <CaseTrack key={m.id} id={m.id!} />;
  else if (p === '/field/court') screen = <CourtPage />;
  else if ((m = match('/field/court/:id', p))) screen = <CourtEvent key={m.id} id={m.id!} />;
  else if (p === '/field/case-files') screen = <CaseFilesPage />;
  else if ((m = match('/field/case-files/:id', p))) screen = <CaseFileDetail key={m.id} id={m.id!} />;
  else if (p === '/field/my-gear') screen = <MyGearPage />;
  else if (p === '/field/spares') screen = <SparesPage />;
  else if (p === '/field/permits') screen = <PermitsPage />;
  else if ((m = match('/field/permits/:id', p))) screen = <PermitDetail key={m.id} id={m.id!} />;
  else if ((m = match('/field/fleet/:id', p))) screen = <VehicleDetail key={m.id} id={m.id!} />;
  else if (p === '/field/equipment/report') screen = <AssetReportPage />;
  else if ((m = match('/field/equipment/:id', p))) screen = <EquipmentDetail key={m.id} id={m.id!} />;
  else if (p === '/field/scan') screen = <ScanScreen papers={data.papers} />;
  else if (p === '/field/transfers') screen = <Transfers online={online} />;
  else if (p === '/field/map')
    screen = <FieldMap rows={data.papers.map((x) => ({ p: x, ev: localEvaluation(x, data.rules, data.outbox) }))} online={online} initial={new URLSearchParams(path.split('?')[1] ?? '').get('paper')} />;
  else screen = <PaperListScreen data={data} pos={pos && pos.accuracy <= 2000 ? pos : null} online={online} />;
  if (!screenInProduct(p)) screen = <p className="muted" style={{ padding: 16 }}>That’s part of BlueHQ. Your agency uses CivilHound.</p>;

  // Arrival dashboard: automatic within 500 ft on the main screens, or forced with "Attempt now".
  const forced = match('/field/arrive/:id', p);
  const arriveRow = forced ? rows.find((r) => r.p.id === forced.id) ?? null : (p === '/field' || p === '/field/map') && arrival.current ? arrival.current : null;
  if (forced && !arriveRow) screen = <Empty>This paper is not on this device.</Empty>;
  const arrivalEl = arriveRow && (
    <ArrivalScreen
      row={arriveRow}
      others={sameAddress(rows, arriveRow)}
      rules={data.rules}
      pending={data.outbox}
      pos={pos}
      online={online}
      auto={!forced}
      onSwitch={(id) => (arrival.dismiss(arriveRow.p.id), navigate(`/field/arrive/${id}`))}
      onClose={() => (arrival.dismiss(arriveRow.p.id), forced ? navigate('/field') : undefined)}
      onFullForm={() => (arrival.dismiss(arriveRow.p.id), navigate(`/field/paper/${arriveRow.p.id}/attempt`))}
      onChanged={doSync}
    />
  );
  if (arrivalEl && !routeMode) screen = arrivalEl;

  if (p === '/field/evidence/labels') return <EvidenceLabels />;
  if (match('/field/reports/:id/print', p)) return <ReportPrint id={match('/field/reports/:id/print', p)!.id!} />;
  if (match('/field/schools/drills/:id/print', p)) return <DrillPrint id={match('/field/schools/drills/:id/print', p)!.id!} />;
  if (match('/field/forms/:id/print', p)) return <FormPrint id={match('/field/forms/:id/print', p)!.id!} />;
  if (match('/field/tows/:id/print', p)) return <TowPrint id={match('/field/tows/:id/print', p)!.id!} />;
  if (match('/field/tickets/:id/print', p)) return <TicketPrint id={match('/field/tickets/:id/print', p)!.id!} />;
  if (locked) return <LockScreen online={online} reason={locked} onUnlock={() => ((idle.current = Date.now()), setLocked(false), refresh())} />;

  const signOut = () => {
    if (pending > 0 && !window.confirm(`${pending} attempt${pending === 1 ? ' is' : 's are'} not synced yet. Signing out now may lose ${pending === 1 ? 'it' : 'them'}. Sign out anyway?`)) return;
    logout();
  };
  // Duty keys belong to civil serve; reports, messages and the rest don't need them.
  const serveScreen = p === '/field' || /^\/field\/(paper|arrive|map|scan|transfers|setout)/.test(p);
  const header = (
    <FieldHeader
      showDuty={serveScreen || p === '/field/home' /* patrol goes on duty from Home (miles, dispatch map) */}
      sideOpen={!(routeMode && (p === '/field' || !!arrivalEl)) && side.roomy ? side.open : undefined}
      title={titleFor(p, routeMode)}
      online={online}
      syncing={syncing}
      syncNote={syncMsg?.tone === 'ok' ? null : syncMsg?.text ?? null}
      pending={pending}
      onSync={doSync}
      onMenu={() => (!(routeMode && (p === '/field' || !!arrivalEl)) && side.roomy ? side.toggle() : setMenu(true))}
      onLock={() => setLocked('manual')}
    />
  );
  const menuEl = (
    <FieldMenu
      open={menu}
      onClose={() => setMenu(false)}
      wide={wide}
      serveMode={routeMode}
      onToggleView={() => (writePhonePref(!phoneView), setPhoneView(!phoneView), navigate('/field'))}
      onSignOut={signOut}
      onCustomize={() => (setMenu(false), navigate('/field'), setCustomizing(true))}
    />
  );
  const customizeEl = customizing && <CustomizeServe ctl={serveLayout} onClose={() => setCustomizing(false)} />;

  if (arrivalEl && routeMode)
    return (
      <DutyProvider online={online}>
        <CheckInOverlay />
        {header}
        {menuEl}
        {customizeEl}
        <div className="arr-wide">{arrivalEl}</div>
      </DutyProvider>
    );

  if (p === '/field' && routeMode)
    return (
      <DutyProvider online={online}>
        <CheckInOverlay />
        <RouteMode
          rows={rows}
          pos={pos}
          posErr={posErr}
          onAttempt={(id) => navigate(`/field/arrive/${id}`)}
          online={online}
          syncing={syncing}
          syncNote={syncMsg?.tone === 'ok' ? null : syncMsg?.text ?? (pending ? `${pending} attempt(s) queued` : null)}
          onSync={doSync}
          header={header}
          layout={serveLayout.layout}
        />
        {menuEl}
        {customizeEl}
      </DutyProvider>
    );

  return (
    <DutyProvider online={online}>
    <div className={`field-app ${side.open ? 'with-side' : ''}`}>
      <CheckInOverlay />
      {header}
      {menuEl}
      {side.open && <FieldSide onSignOut={signOut} />}
      {customizeEl}
      {syncMsg && (
        <div className={`alert ${syncMsg.tone} flush`} role="status">
          {syncMsg.text}
        </div>
      )}
      {flagged.length > 0 && (
        <div className="alert warn flush">
          {flagged.length} offline update(s) need attention:
          <ul>
            {flagged.map((f) => (
              <li key={f.clientId}>
                {OUTCOME_LABELS[f.payload.outcome as AttemptOutcome]} — {f.state === 'held' ? 'held for supervisor review' : 'rejected'}: {f.message}{' '}
                <button className="btn link sm" onClick={() => idb.del('outbox', f.clientId).then(data.reload)}>
                  dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {serveScreen && <DutyBar />}
      <main className={`field-main ${['/field/schedule', '/field/stats', '/field/home', '/field/resources', '/field/signature', '/field/security', '/field/reports', '/field/admin', '/field/issued'].includes(p) || /^\/field\/(reports|people|photos|warrants|messages|directory|cases|court|case-files|my-gear|spares|permits|fleet|equipment|evidence|tows|tickets|civil-search|evictions|on-call|phones|training|pio|tips|intel|uof|personnel|ews|hr|evaluations|inspections|alerts|narcan|schools|forms|escorts|bolos|applicants|bwc|events|arrests|shutoffs|policies|mileage|call-numbers|dues|house-checks|daily-log|ia\b|patrol-map|command|resources)/.test(p) ? 'wide' : ''}`}>{screen}</main>
    </div>
    </DutyProvider>
  );
}

function LockScreen({ online, onUnlock, reason }: { online: boolean; onUnlock: () => void; reason: 'idle' | 'manual' }) {
  const { me } = useAuth();
  const [pw, setPw] = useState('');
  const a = useAction();
  return (
    <div className="auth-shell">
      <div className="auth-card stack">
        <h2>Locked</h2>
        <p className="muted">{reason === 'manual' ? 'Screen locked.' : 'Locked after inactivity.'} {me?.user.displayName}</p>
        {online ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                await api.post('/api/auth/login', { agencyCode: me!.agency.code, username: me!.user.username, password: pw });
                onUnlock();
              });
            }}
          >
            <Field label="Password">
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus required />
            </Field>
            <button className="btn primary">Unlock</button>
            {passkeysSupported() && (
              <button type="button" className="btn pk-btn" disabled={a.busy} onClick={() => a.run(async () => (await signInWithPasskey(me!.user.id), onUnlock()))}>
                <Icon name="fingerprint" size={18} /> Unlock with fingerprint
              </button>
            )}
            <ErrorBox error={a.error} />
          </form>
        ) : (
          <>
            <Notice tone="warn">Offline: the native app unlocks with Face ID / Touch ID. This web prototype relies on the device passcode lock (MOB-013).</Notice>
            <button className="btn primary" onClick={onUnlock}>
              Unlock
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Screens ────────────────────────────────────────────────────────────────
function PaperListScreen({ data, pos, online }: { data: ReturnType<typeof useFieldData>; pos: { lat: number; lng: number } | null; online: boolean }) {
  const { navigate } = useRouter();
  const { me } = useAuth();
  const tz = me?.agency.timezone ?? 'America/New_York';
  const today = localParts(new Date(), tz).date;
  const [wm, setWm] = useWorkMode(me?.user.id, today);
  const [picking, setPicking] = useState(false);
  const [reminders, setReminders] = useState<any[]>([]);
  useEffect(() => {
    api.get('/api/me/reminders').then((r) => setReminders(r.reminders)).catch(() => undefined);
  }, []);
  const rows = useMemo(
    () =>
      data.papers
        .map((p) => ({ p, ev: localEvaluation(p, data.rules, data.outbox) }))
        .sort((a, b) => (b.ev?.urgency?.score ?? 0) - (a.ev?.urgency?.score ?? 0)),
    [data.papers, data.rules, data.outbox],
  );
  const atOffice = new Set<string>(data.pickups.filter((x) => x.source === 'office').map((x) => x.paper_id));
  const pickup = rows.filter((r) => atOffice.has(r.p.id));
  const openRows = rows.filter((r) => ['ASSIGNED', 'IN_SERVICE'].includes(r.p.status) && !atOffice.has(r.p.id));
  const orderItems = openRows.map((r) => ({ id: r.p.id, pin: pinOf(r.p), address: r.p.address, est: estimateService(r.p.serveStats, new Date(), tz), open: true, eligibleNow: eligibilityNow(r.p, data.rules, data.outbox, new Date()).bucket === 'now' }));
  const wmOrder = orderFor(orderItems, wm, pos);
  const wmIndex = wmOrder ? new Map(wmOrder.map((id, i) => [id, i])) : null;
  const open = wmIndex ? [...openRows].sort((a, b) => (wmIndex.get(a.p.id) ?? 1e9) - (wmIndex.get(b.p.id) ?? 1e9)) : openRows;
  const done = rows.filter((r) => !['ASSIGNED', 'IN_SERVICE'].includes(r.p.status));
  return (
    <div className="stack">
      {reminders.map((r) => (
        <Notice key={r.paperId} tone="warn">
          {r.civilosNumber}: {r.daysRemaining} day(s) to service cutoff — {r.missing[0]}
        </Notice>
      ))}
      <div className="row between">
        <h1>My papers</h1>
        <span className="muted small">{data.last ? `Synced ${fmtDateTime(data.last.at)}` : 'Not synced yet'}</span>
      </div>
      {rows.length === 0 && <Empty>No papers on this device. Tap “Sync now” when online.</Empty>}
      {openRows.length > 0 && (picking || !wm) && (
        <WorkModePicker today={today} items={orderItems} pos={pos} online={online} current={wm} onChoose={(w) => (setWm(w), setPicking(false))} onCancel={wm ? () => setPicking(false) : undefined} />
      )}
      {openRows.length > 0 && wm && !picking && <WorkModeBar wm={wm} items={orderItems} pos={pos} order={wmOrder} onChange={setWm} onPick={() => setPicking(true)} />}
      {open.map(({ p, ev }) => (
        <PaperRowCard key={p.id} p={p} ev={ev} pending={data.outbox.filter((o) => o.paperId === p.id && o.state === 'pending').length} onOpen={() => navigate(`/field/paper/${p.id}`)} />
      ))}
      {pickup.length > 0 && <h2>To pick up at the office ({pickup.length})</h2>}
      {pickup.map(({ p, ev }) => (
        <PaperRowCard key={p.id} p={p} ev={ev} pending={0} onOpen={() => navigate(`/field/paper/${p.id}`)} />
      ))}
      {done.length > 0 && <h2>Completed — affidavit / return</h2>}
      {done.map(({ p, ev }) => (
        <PaperRowCard key={p.id} p={p} ev={ev} pending={0} onOpen={() => navigate(`/field/paper/${p.id}`)} />
      ))}
    </div>
  );
}

function PaperRowCard({ p, ev, pending, onOpen }: { p: any; ev: any; pending: number; onOpen: () => void }) {
  const { me } = useAuth();
  const allowed = ev?.attemptNow?.allowed;
  return (
    <button className="paper-card" onClick={onOpen}>
      <div className="pc-top">
        <span className="pc-name">{p.defendant.full_name}</span>
        <StatusBadge status={p.status} labels={me?.agency.statusLabels} />
      </div>
      <div className="pc-addr">{[p.address.line1, p.address.city].filter(Boolean).join(', ')}</div>
      <div className="pc-meta">
        <span className="mono">{p.civilosNumber}</span>
        <span>{p.evaluation?.paperType?.name}</span>
      </div>
      <div className="row gap wrap small pc-status">
        <span className={`dot ${allowed ? 'green' : ['ASSIGNED', 'IN_SERVICE'].includes(p.status) ? 'gray' : 'none'}`} />
        {['ASSIGNED', 'IN_SERVICE'].includes(p.status) && (allowed ? (wontCount(ev) ? 'Non-qualified attempt now' : 'Eligible now') : 'Not eligible now')}
        <span className="muted">{p.attempts.length + pending} attempt{p.attempts.length + pending === 1 ? '' : 's'}</span>
        {pending > 0 && <Badge tone="warn">{pending} queued</Badge>}
        {ev?.urgency?.tier !== 'normal' && <Badge tone="bad">{ev?.urgency?.tier === 'supervisor_urgent' ? 'Urgent' : 'Priority'}</Badge>}
        {p.courtDate && <span className="muted">Court {fmtDate(p.courtDate)}</span>}
      </div>
      {['ASSIGNED', 'IN_SERVICE'].includes(p.status) && <ServiceCircles ev={ev} />}
    </button>
  );
}

function DocViewer({ doc }: { doc: any }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null;
    idb.get<any>('docs', doc.id).then((d) => {
      if (d) {
        u = URL.createObjectURL(d.blob);
        setUrl(u);
      }
    });
    return () => {
      if (u) URL.revokeObjectURL(u);
    };
  }, [doc.id]);
  if (!url) return <p className="muted small">Not cached on this device yet — sync while online.</p>;
  return doc.content_type.startsWith('image/') ? <img src={url} alt={doc.filename} className="doc-img" /> : <iframe title={doc.filename} src={url} className="doc-frame" />;
}

function FieldPaper({ id, data, online, onChanged, onCompleted }: { id: string; data: ReturnType<typeof useFieldData>; online: boolean; onChanged: () => void; onCompleted: (msg: string) => void }) {
  const { navigate } = useRouter();
  const p = data.papers.find((x) => x.id === id);
  const [open, setOpen] = useState<string | null>(null);
  const [live, setLive] = useState<any>(null);
  const a = useAction();
  useEffect(() => {
    if (online && p && !['ASSIGNED', 'IN_SERVICE'].includes(p.status)) api.get(`/api/papers/${id}`).then(setLive).catch(() => undefined);
  }, [online, id, p?.status, p?.version]);
  if (!p) return <Empty>This paper is not on this device. It may have been reassigned, returned or completed.</Empty>;
  const ev = localEvaluation(p, data.rules, data.outbox);
  const pending = data.outbox.filter((o) => o.paperId === id);
  const pin = p.address.pin_lat != null ? [p.address.pin_lat, p.address.pin_lng] : p.address.lat != null ? [p.address.lat, p.address.lng] : null;
  const addr = [p.address.line1, p.address.line2, p.address.city, p.address.state, p.address.zip].filter(Boolean).join(', ');
  const attemptable = ['ASSIGNED', 'IN_SERVICE'].includes(p.status);
  return (
    <div className="stack fpaper">
      <header className="fp-head">
        <div className="fp-top">
          <h1 className="mono">{p.civilosNumber}</h1>
          <StatusBadge status={p.status} />
          {p.onHold && <Badge tone="bad">On hold</Badge>}
        </div>
        <div className="fp-name">{p.defendant.full_name}</div>
        <div className="fp-type">{p.evaluation.paperType.name}</div>
        <div className="fp-addr">{addr}</div>
        <div className="fp-meta">
          <span>
            Case <span className="mono">{p.case.case_number}</span>
          </span>
          {p.courtDate && <span>Court {fmtDate(p.courtDate)}</span>}
        </div>
        <div className="fp-actions">
          <a className="btn" href={pin ? `https://www.google.com/maps/dir/?api=1&destination=${pin[0]},${pin[1]}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer">
            <Icon name="car" size={17} /> Navigate
          </a>
          {pin && (
            <button className="btn" onClick={() => navigate(`/field/map?paper=${id}`)}>
              <Icon name="map" size={17} /> Show on map
            </button>
          )}
        </div>
      </header>
      {p.specialInstructions && <Notice tone="warn">{p.specialInstructions}</Notice>}
      <Card title="Safety alerts">
        <SafetyAlerts paper={p} online={online} onChanged={onChanged} />
      </Card>
      <Card title={ev.local ? 'Rules (evaluated on device)' : 'Rules'}>
        <RulesPanel ev={ev} />
      </Card>
      {attemptable && online && <OnSceneControls paperId={id} address={p.address.line1} />}
      {attemptable && (
        <div className="fp-bar">
          <button className="btn" onClick={() => navigate(`/field/paper/${id}/attempt`)} disabled={p.onHold || p.recalled}>
            Full attempt form
          </button>
          <button className="btn primary" onClick={() => navigate(`/field/arrive/${id}`)} disabled={p.onHold || p.recalled}>
            Attempt this paper now
          </button>
        </div>
      )}
      {attemptable && ev.nonService.satisfied && ev.nonService.basis === 'attempts' && (
        <button
          className="btn big"
          disabled={!online || a.busy}
          onClick={() =>
            a.run(async () => {
              await api.post(`/api/papers/${id}/non-service`);
              onChanged();
            })
          }
        >
          {online ? 'Finalize non-service' : 'Finalize non-service (needs connection)'}
        </button>
      )}
      <ErrorBox error={a.error} />
      {!attemptable && (online ? live ? <AffidavitPanel paperId={id} paperStatus={live.paper.status} affidavits={live.affidavits} onChanged={async () => {
          const d = await api.get(`/api/papers/${id}`);
          setLive(d);
          if (d.paper.status === 'AFFIDAVIT_SIGNED') onCompleted(`Affidavit signed for ${p.civilosNumber}. The paper is complete and has been removed from this device — return the physical paper to the office.`);
        }} /> : null : <Notice>Affidavit signing needs a connection.</Notice>)}
      <Card title={`Attempts (${p.attempts.length + pending.length})`}>
        {p.attempts.length + pending.length === 0 && <Empty>No attempts yet.</Empty>}
        <ul className="plain">
          {p.attempts.map((x: any) => (
            <li key={x.id}>
              {fmtDateTime(x.attempted_at)} — {OUTCOME_LABELS[x.outcome as AttemptOutcome]} {x.method ? `(${x.method})` : ''} {x.source === 'offline' && <Badge>offline</Badge>}
              {x.review_status === 'needs_review' && <Badge tone="bad">needs review</Badge>}
            </li>
          ))}
          {pending.map((o) => (
            <li key={o.clientId}>
              {fmtDateTime(o.payload.attemptedAt as string)} — {OUTCOME_LABELS[o.payload.outcome as AttemptOutcome]} <Badge tone={o.state === 'pending' ? 'warn' : 'bad'}>{o.state === 'pending' ? 'queued' : o.state}</Badge>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Documents">
        {p.documents.length === 0 && <Empty>No documents.</Empty>}
        {p.documents.map((d: any) => (
          <div key={d.id} className="stack sm">
            <button className="btn sm" onClick={() => setOpen(open === d.id ? null : d.id)}>
              {open === d.id ? 'Hide' : 'View'} {d.filename}
            </button>
            {open === d.id && <DocViewer doc={d} />}
          </div>
        ))}
        <p className="small muted">Documents are viewable in the app only and are removed from this device when the paper leaves your assignment.</p>
      </Card>
    </div>
  );
}

const UNSUCCESSFUL: AttemptOutcome[] = ['no_answer', 'not_home', 'gated_no_access', 'other_unsuccessful'];
const IMMEDIATE: AttemptOutcome[] = ['bad_address', 'outside_service_area', 'moved', 'vacant', 'deceased', 'unable_to_locate'];

function useGps() {
  const [gps, setGps] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const capture = useCallback(() => {
    if (!('geolocation' in navigator)) return setErr('GPS not available on this device');
    setErr(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      (e) => setErr(e.message || 'Could not get location'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
  }, []);
  useEffect(capture, [capture]);
  return { gps, err, capture };
}

function AttemptForm({ id, data, online }: { id: string; data: ReturnType<typeof useFieldData>; online: boolean }) {
  const { navigate } = useRouter();
  const duty = useDuty();
  const p = data.papers.find((x) => x.id === id);
  const { gps, err: gpsErr, capture } = useGps();
  const [outcome, setOutcome] = useState<AttemptOutcome | ''>('');
  const [method, setMethod] = useState<'personal' | 'substitute' | 'posting' | ''>('');
  const [r, setR] = useState({ name: '', relationship: '', age: '', estimated: true, residesWithDefendant: false, competent: false, notAParty: false });
  const [notes, setNotes] = useState('');
  const [vehicles, setVehicles] = useState<VehicleDraft[]>([]);
  const [ohx, setOhx] = useState('');
  const [explain, setExplain] = useState('');
  const [prop, setProp] = useState({ found: false, description: '', mayQualifyForLiquidation: false });
  const [supAction, setSupAction] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [needExplain, setNeedExplain] = useState(false);
  const clientId = useRef(newId());
  const a = useAction();
  if (!p) return <Empty>Paper not on this device.</Empty>;
  const cfg = data.rules[p.ruleVersionId];
  const type = cfg ? getPaperType(cfg, p.paperTypeCode) : null;
  const ev = localEvaluation(p, data.rules, data.outbox);
  const recipient =
    method === 'substitute'
      ? { name: r.name, relationship: r.relationship, age: r.estimated ? null : Number(r.age) || null, estimatedAge: r.estimated ? Number(r.age) || null : null, residesWithDefendant: r.residesWithDefendant, competent: r.competent, notAParty: r.notAParty }
      : null;
  const subIssues = type && method === 'substitute' ? validateSubstituteRecipient(type, recipient) : [];
  const localCheck = cfg && outcome ? evaluateAttempt(cfg, { paperTypeCode: p.paperTypeCode, phase: phaseOf(p.status), onHold: p.onHold, recalled: p.recalled, attempts: ev.attempts ?? [] }, { at: new Date(), outcome, method: method || null, calendarOverride: p.calendarOverrideDate === localParts(new Date(), cfg.timezone).date }) : null;

  const startVoice = () => {
    const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.onresult = (e: any) => setNotes((n) => `${n}${n ? ' ' : ''}${e.results[0][0].transcript}`);
    rec.start();
  };

  const submit = () =>
    a.run(async () => {
      const payload: Record<string, unknown> = {
        clientId: clientId.current,
        attemptedAt: new Date().toISOString(),
        outcome,
        method: outcome === 'served' ? method : undefined,
        recipient: recipient ?? undefined,
        notes: notes || undefined,
        outsideHoursExplanation: ohx || undefined,
        gps: gps ?? undefined,
        property: outcome === 'property_documented' ? prop : undefined,
        supervisorAction: outcome === 'supervisor_action' ? supAction : undefined,
        serverExplanation: explain || undefined,
        vehicles: vehiclesPayload(vehicles),
      };
      try {
        const res = await submitAttempt(p.id, p.version, payload);
        setResult(res.mode === 'queued' ? 'Saved on this device. It will upload automatically when you are back online.' : `Recorded. Paper is now ${res.result.paperStatus.replace('_', ' ').toLowerCase()}.${res.result.gps?.outsideRadius ? ` ${res.result.gps.message}` : ''}`);
        await data.reload();
        duty.refresh(); // recording the attempt ends "out of car" and counts as activity
      } catch (e) {
        if (e instanceof ApiError && e.details?.code === 'GPS_ANOMALY_EXPLANATION_REQUIRED') setNeedExplain(true);
        throw e;
      }
    });

  if (result)
    return (
      <div className="stack">
        <Notice tone="ok">{result}</Notice>
        <button className="btn primary big" onClick={() => navigate(`/field/paper/${id}`)}>
          Back to paper
        </button>
        <button className="btn big" onClick={() => navigate('/field')}>
          Next paper
        </button>
      </div>
    );

  const canSubmit = outcome && (outcome !== 'served' || method) && subIssues.length === 0 && (!localCheck || localCheck.allowed) && (!needExplain || explain.trim());
  return (
    <form
      data-unsaved={outcome || notes ? 'true' : undefined}
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h1>Record attempt</h1>
      <div className="muted">
        {p.civilosNumber} · {p.defendant.full_name}
      </div>
      {!online && <Notice tone="warn">Offline — the attempt will be saved on this device and uploaded later.</Notice>}
      <div className="gps">
        {gps ? (
          <span>
            GPS fixed ±{gps.accuracy} m
          </span>
        ) : (
          <span className="muted">{gpsErr ?? 'Getting GPS…'}</span>
        )}
        <button type="button" className="btn sm ghost" onClick={capture}>
          Refresh
        </button>
      </div>
      <Field label="Outcome">
        <select value={outcome} onChange={(e) => setOutcome(e.target.value as AttemptOutcome)} required>
          <option value="">Select…</option>
          <option value="served">Served</option>
          <optgroup label="Not served">
            {UNSUCCESSFUL.map((o) => (
              <option key={o} value={o}>
                {OUTCOME_LABELS[o]}
              </option>
            ))}
          </optgroup>
          <optgroup label="Immediate disposition">
            {IMMEDIATE.filter((o) => !type || type.immediateDispositions.includes(o) || type.supervisorReviewDispositions.includes(o)).map((o) => (
              <option key={o} value={o}>
                {OUTCOME_LABELS[o]}
                {o === 'unable_to_locate' ? ' (supervisor review)' : ''}
              </option>
            ))}
          </optgroup>
          {type?.completion === 'property_documentation' && <option value="property_documented">{OUTCOME_LABELS.property_documented}</option>}
          {type?.completion === 'supervisor_action' && <option value="supervisor_action">{OUTCOME_LABELS.supervisor_action}</option>}
        </select>
      </Field>
      {outcome === 'served' && (
        <div className="field" role="group" aria-labelledby="method-label">
          <span className="field-l" id="method-label">Service method</span>
          <div className="seg">
            <button type="button" className={method === 'personal' ? 'active' : ''} onClick={() => setMethod('personal')}>
              Personal
            </button>
            {type?.substituteAllowed && (
              <button type="button" className={method === 'substitute' ? 'active' : ''} onClick={() => setMethod('substitute')}>
                Substitute
              </button>
            )}
            {type && type.posting !== 'never' && (
              <button type="button" className={method === 'posting' ? 'active' : ''} onClick={() => setMethod('posting')}>
                Posting
              </button>
            )}
          </div>
          {type && !type.substituteAllowed && <span className="hint">{type.name}: personal service only{type.posting !== 'never' ? ' or posting' : ''}.</span>}
        </div>
      )}
      {method === 'substitute' && outcome === 'served' && (
        <Card title="Substitute recipient">
          <div className="stack sm">
            <Field label="Name">
              <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} />
            </Field>
            <Field label="Relationship to defendant">
              <input value={r.relationship} onChange={(e) => setR({ ...r, relationship: e.target.value })} />
            </Field>
            <div className="row gap">
              <Field label="Age">
                <input inputMode="numeric" value={r.age} onChange={(e) => setR({ ...r, age: e.target.value.replace(/\D/g, '') })} style={{ width: 80 }} />
              </Field>
              <label className="row gap-sm">
                <input type="checkbox" checked={r.estimated} onChange={(e) => setR({ ...r, estimated: e.target.checked })} /> estimated
              </label>
            </div>
            <label className="row gap-sm">
              <input type="checkbox" checked={r.residesWithDefendant} onChange={(e) => setR({ ...r, residesWithDefendant: e.target.checked })} /> Lives at this address with the defendant
            </label>
            <label className="row gap-sm">
              <input type="checkbox" checked={r.competent} onChange={(e) => setR({ ...r, competent: e.target.checked })} /> Of competent mind
            </label>
            <label className="row gap-sm">
              <input type="checkbox" checked={r.notAParty} onChange={(e) => setR({ ...r, notAParty: e.target.checked })} /> Not a party to the action
            </label>
            {subIssues.length > 0 && (
              <ul className="issues">
                {subIssues.map((i) => (
                  <li key={i.code} className="warn">
                    {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}
      {outcome === 'property_documented' && (
        <Card title="Property (Writ of Execution)">
          <label className="row gap-sm">
            <input type="checkbox" checked={prop.found} onChange={(e) => setProp({ ...prop, found: e.target.checked })} /> Property found
          </label>
          <Field label="Description">
            <textarea rows={3} value={prop.description} onChange={(e) => setProp({ ...prop, description: e.target.value })} />
          </Field>
          <label className="row gap-sm">
            <input type="checkbox" checked={prop.mayQualifyForLiquidation} onChange={(e) => setProp({ ...prop, mayQualifyForLiquidation: e.target.checked })} /> May qualify for liquidation
          </label>
        </Card>
      )}
      {outcome === 'supervisor_action' && (
        <Field label="Situation / action needed">
          <textarea rows={3} value={supAction} onChange={(e) => setSupAction(e.target.value)} />
        </Field>
      )}
      <VehiclesInYard value={vehicles} onChange={setVehicles} />
      <div className="row gap">
        <div className="grow">
          <Field label="Notes">
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        {((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition) && (
          <button type="button" className="btn sm" onClick={startVoice} aria-label="Dictate notes (voice to text)">
            🎤
          </button>
        )}
      </div>
      {localCheck && localCheck.outsideServiceHours && (
        <Field label="Outside normal hours — explanation (optional)">
          <input value={ohx} onChange={(e) => setOhx(e.target.value)} />
        </Field>
      )}
      {localCheck && (localCheck.blockers.length > 0 || localCheck.warnings.length > 0) && (
        <ul className="issues">
          {localCheck.blockers.map((b) => (
            <li key={b.code} className="bad">
              {b.message}
            </li>
          ))}
          {localCheck.warnings.map((w) => (
            <li key={w.code} className="warn">
              {w.message}
            </li>
          ))}
        </ul>
      )}
      {needExplain && (
        <Field label="GPS flagged — explain this attempt (required)">
          <textarea rows={2} value={explain} onChange={(e) => setExplain(e.target.value)} />
        </Field>
      )}
      <ErrorBox error={a.error} />
      <button className="btn primary big" disabled={!canSubmit || a.busy}>
        {a.busy ? 'Saving…' : 'Save attempt'}
      </button>
      <button type="button" className="btn big ghost" onClick={() => navigate(`/field/paper/${id}`)}>
        Cancel
      </button>
    </form>
  );
}

function ScanScreen({ papers }: { papers: any[] }) {
  const { navigate } = useRouter();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const supported = 'BarcodeDetector' in window;
  const open = (raw: string) => {
    const n = normalizeScan(raw);
    const p = papers.find((x) => x.civilosNumber === n);
    if (p) navigate(`/field/paper/${p.id}`);
    else setMsg(`${n} is not assigned to you on this device. If you just received it, sync first.`);
  };
  useEffect(() => {
    if (!camera || !supported) return;
    let stream: MediaStream | null = null;
    let stop = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        video.current!.srcObject = stream;
        await video.current!.play();
        const det = new (window as any).BarcodeDetector({ formats: ['code_128', 'qr_code'] });
        while (!stop) {
          const found = await det.detect(video.current!).catch(() => []);
          if (found[0]?.rawValue) {
            open(found[0].rawValue);
            break;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (e) {
        setMsg(`Camera unavailable: ${(e as Error).message}`);
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [camera]);
  return (
    <div className="stack">
      <h1>Scan paper</h1>
      {supported ? (
        <button className="btn big" onClick={() => setCamera(!camera)}>
          {camera ? 'Stop camera' : 'Scan with camera'}
        </button>
      ) : (
        <Notice>Camera scanning isn’t supported in this browser; the native app uses the camera. Type the number or use a Bluetooth scanner.</Notice>
      )}
      {camera && <video ref={video} className="scan-video" playsInline muted />}
      <form
        className="row gap"
        onSubmit={(e) => {
          e.preventDefault();
          open(code);
        }}
      >
        <input className="grow scan-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paper number" autoFocus />
        <button className="btn primary">Open</button>
      </form>
      {msg && <Notice tone="warn">{msg}</Notice>}
    </div>
  );
}

function Transfers({ online }: { online: boolean }) {
  const { me } = useAuth();
  const [list, setList] = useState<any[] | null>(null);
  const a = useAction();
  const load = () => api.get('/api/custody/transfers').then((r) => setList(r.transfers)).catch(() => setList(null));
  useEffect(() => {
    if (online) load();
  }, [online]);
  if (!online) return <Notice>Transfers need a connection.</Notice>;
  const act = (t: any, kind: 'scan-out' | 'scan-in') => {
    const n = window.prompt(`Scan or type ${t.civilos_number} to confirm`);
    if (n) a.run(async () => {
      await api.post(`/api/custody/transfers/${t.id}/${kind}`, { paperNumber: n });
      await syncNow().catch(() => undefined);
      load();
    });
  };
  return (
    <div className="stack">
      <h1>Field transfers</h1>
      <ErrorBox error={a.error} />
      {list?.length === 0 && <Empty>No pending transfers.</Empty>}
      {list?.map((t) => (
        <Card key={t.id} title={<span className="mono">{t.civilos_number}</span>}>
          <p>
            {t.from_name} → {t.to_name} · {t.status.replace('_', ' ')}
          </p>
          {t.from_server_id === me?.user.id && t.status === 'authorized' && (
            <button className="btn primary" onClick={() => act(t, 'scan-out')}>
              Scan out (hand over)
            </button>
          )}
          {t.to_server_id === me?.user.id && t.status === 'in_transit' && (
            <button className="btn primary" onClick={() => act(t, 'scan-in')}>
              Scan in (receive)
            </button>
          )}
          {t.to_server_id === me?.user.id && t.status === 'authorized' && <p className="muted small">Waiting for {t.from_name} to scan out.</p>}
        </Card>
      ))}
    </div>
  );
}
